import { Organization as SubscriptionOrganization, SubscriptionStatusEnum } from "@syadem/kairos-subscription-js";
import { Organization } from "../../../domain/organization";
import { Team } from "../../../domain/team";

export interface ImportDestinationOption {
  id: string;
  label: string;
  organizationId: string | null;
  teamId: string | null;
}

export function buildImportDestinationOptions({
  organizations,
  subscriptionOrganizations,
  teams,
  canImportToLiberal,
  liberalLabel,
}: {
  organizations?: { [id: string]: Organization };
  subscriptionOrganizations?: { [id: string]: SubscriptionOrganization };
  teams?: { [id: string]: Team };
  canImportToLiberal: boolean;
  liberalLabel: string;
}): ImportDestinationOption[] {
  const destinations: ImportDestinationOption[] = [];

  if (canImportToLiberal) {
    destinations.push({
      id: "liberal",
      label: liberalLabel,
      organizationId: null,
      teamId: null,
    });
  }

  if (!organizations || !teams) {
    return destinations;
  }

  const teamDestinations = Object.values(teams)
    .filter(
      (team) =>
        team.activated &&
        hasActiveSubscription(subscriptionOrganizations?.[team.organizationId]),
    )
    .map((team) => ({
      id: buildTeamDestinationId(team.organizationId, team.id),
      label: buildTeamDestinationLabel(organizations[team.organizationId]?.name, team.name),
      organizationId: team.organizationId,
      teamId: team.id,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

  return [...destinations, ...teamDestinations];
}

export function getPreferredImportDestinationId({
  destinations,
  currentOrganizationId,
  currentTeamId,
}: {
  destinations: ImportDestinationOption[];
  currentOrganizationId: string | null;
  currentTeamId: string | null;
}): string {
  if (destinations.some((destination) => destination.id === "liberal")) {
    return "liberal";
  }

  if (currentOrganizationId && currentTeamId) {
    const currentTeamDestinationId = buildTeamDestinationId(currentOrganizationId, currentTeamId);
    if (destinations.some((destination) => destination.id === currentTeamDestinationId)) {
      return currentTeamDestinationId;
    }
  }

  return destinations[0]?.id ?? "";
}

export function buildHealthRecordPath(destination: ImportDestinationOption, healthRecordId: string): string {
  if (destination.organizationId && destination.teamId) {
    return `/organizations/${destination.organizationId}/teams/${destination.teamId}/health-records/${healthRecordId}`;
  }

  return `/liberal/health-records/${healthRecordId}`;
}

export function buildTeamDestinationId(organizationId: string, teamId: string): string {
  return `team:${organizationId}:${teamId}`;
}

function buildTeamDestinationLabel(organizationName: string | undefined, teamName: string): string {
  return organizationName ? `${organizationName} - ${teamName}` : teamName;
}

function hasActiveSubscription(subscriptionOrganization: SubscriptionOrganization | undefined): boolean {
  if (!subscriptionOrganization) {
    return false;
  }

  return (
    subscriptionOrganization.subscription.status === SubscriptionStatusEnum.Active ||
    subscriptionOrganization.subscription.status === SubscriptionStatusEnum.Trialing
  );
}
